// ---------------------------------------------------------------------------
// Global search (Cmd/Ctrl+K) — tasks across every project you can see, plus
// quick project jumps. Arrow keys to move, Enter to open, Esc to close.
// ---------------------------------------------------------------------------
// Two depths. The fast path (always on) matches task NAMES and tags in one Odoo
// read. Tab escalates to DEEP search, which also looks inside descriptions and
// comments/notes — several bounded reads, so it's opt-in rather than firing on
// every keystroke. Snippets arrive as plain text plus match offsets; we build the
// highlight from those offsets and never render server HTML here.
// ---------------------------------------------------------------------------
// It's also a palette you ACT in (DESIGN_DIRECTION §5C):
//   · ZERO QUERY is no longer dead text — it lists your recent tasks (the
//     'cgp:recent-tasks' ring taskDetailStore writes on every open) and four
//     Go-to commands with their real keymap chords.
//   · '→' on a highlighted task row (only when the caret has nowhere left to
//     go, so text editing is untouched) slides into a VERB list — Complete,
//     Assign to…, Move to stage…, Priority, Due dates, Copy link — all through
//     the existing mutations. '←' (or Backspace) walks back; typing anything
//     drops you straight back into search with the keystroke applied.
//   Verbs that need the task's CURRENT values (assignees, priority) resolve
//   them cheapest-first: the board row → the instant-open cache (warmed by the
//   same hover-prefetch below) → one fetch. Assign NEVER blind-replaces the
//   assignee list — that would silently unassign people we couldn't see.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import type { TaskRef, SearchStatus, DeepSearchResult, DeepHit, DeepCommentHit, TaskDetail, TaskListItem, ApprovalState, Assignee, Priority, Stage } from '@cg-projects/shared';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3, CalendarDays, Check, ChevronLeft, ChevronRight, History,
  Image as ImageIcon, Inbox, Kanban, Link as LinkIcon, Search, Star, Target, Users, X,
} from 'lucide-react';
import { api } from '@/core/api/apiClient';
import { nav } from '@/core/router/navIntent';
import { taskLink } from '@/core/router/route';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useUiStore } from '@/core/ui/uiStore';
import { useToastStore } from '@/core/ui/toastStore';
import { completeWithUndo, moveStage, patchTask } from '@/features/tasks/mutations';
import { peekTaskDetail, prefetchTask, useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { Avatar, Kbd, StateBadge } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

/** One entry of the recents ring — the shared 'cgp:recent-tasks' contract.
 *  WRITER: taskDetailStore (on every task open). READER: this palette. */
interface RecentTask { id: number; name: string; areaId: number }
const RECENTS_KEY = 'cgp:recent-tasks';

function readRecents(): RecentTask[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as unknown;
    if (!Array.isArray(list)) return [];
    return list
      .filter((r): r is RecentTask =>
        !!r && typeof (r as RecentTask).id === 'number'
        && typeof (r as RecentTask).name === 'string'
        && typeof (r as RecentTask).areaId === 'number')
      .slice(0, 15);
  } catch { return []; }
}

/** The four Go-to commands of the zero-query palette. Kbd hints are the REAL
 *  chords from Layout's keymap (g then i/m/r/d) — a hint that lies teaches the
 *  wrong reflex. run() reads the store at call time, not module-eval time. */
interface Command { id: string; label: string; kbd: string; icon: LucideIcon; run: () => void }
const COMMANDS: Command[] = [
  { id: 'inbox', label: 'Inbox', kbd: 'g i', icon: Inbox, run: () => useUiStore.getState().openInbox() },
  { id: 'mywork', label: 'My Work', kbd: 'g m', icon: Target, run: () => useUiStore.getState().openMyWork() },
  { id: 'reports', label: 'Reports', kbd: 'g r', icon: BarChart3, run: () => useUiStore.getState().openReports() },
  { id: 'media', label: 'Media', kbd: 'g d', icon: ImageIcon, run: () => useUiStore.getState().openMedia() },
];

type Row =
  | { kind: 'recent'; task: RecentTask }
  | { kind: 'command'; cmd: Command }
  | { kind: 'area'; id: number; name: string }
  | { kind: 'task'; task: TaskRef }
  | { kind: 'desc'; hit: DeepHit }
  | { kind: 'comment'; hit: DeepCommentHit };

const CLOSED = new Set(['1_done', '1_canceled']);

// Hover-intent prefetch (instant-open layer 3): 100ms of dwell on a result row
// warms the task-detail cache so picking it renders with no spinner. One timer
// is enough — the pointer only hovers one row at a time. Touch-only devices
// never produce a useful mouseenter, so they skip the wiring entirely.
// (typeof-guarded: the client unit tests evaluate modules in Node.)
const HOVERABLE = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: hover)').matches;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
const hoverStart = (id: number) => {
  if (!HOVERABLE) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchTask(id), 100);
};
const hoverEnd = () => clearTimeout(hoverTimer);

/* ------------------------------------------------------------------------- */
/* The verb layer — every action flows through the EXISTING mutations.        */
/* ------------------------------------------------------------------------- */

/** The minimum a verb needs to act on a task. Search rows carry state; recents
 *  don't — every consumer treats state as best-effort. */
interface Taskish { id: number; name: string; areaId: number; state?: ApprovalState }

function taskishOf(row: Row | undefined): Taskish | null {
  if (!row) return null;
  if (row.kind === 'task') return { id: row.task.id, name: row.task.name, areaId: row.task.areaId, state: row.task.state };
  if (row.kind === 'desc' || row.kind === 'comment') return { id: row.hit.taskId, name: row.hit.name, areaId: row.hit.areaId, state: row.hit.state };
  if (row.kind === 'recent') return { id: row.task.id, name: row.task.name, areaId: row.task.areaId };
  return null;
}

/** The task's board row wherever a loaded area holds it (same walk the
 *  mutations module does privately). */
function boardRowOf(id: number): TaskListItem | undefined {
  for (const list of Object.values(useTasksStore.getState().tasksByArea)) {
    const found = list.find((t) => t.id === id);
    if (found) return found;
  }
  return undefined;
}

/** Current field values, cheapest source first; ONE fetch only when the task
 *  is in no loaded board and the instant-open cache is cold. Verbs that write
 *  a full list (assignees) must never guess — a blind replace unassigns
 *  people this client simply hadn't loaded. */
async function resolveCurrent(id: number): Promise<Pick<TaskListItem, 'assignees' | 'priority' | 'state' | 'stageId'> | null> {
  const row = boardRowOf(id) ?? peekTaskDetail(id);
  if (row) return row;
  try { return (await api.get<{ task: TaskDetail }>(`/tasks/${id}`)).task; } catch { return null; }
}

const toast = (message: string, kind: 'error' | 'info' = 'info') => useToastStore.getState().show(message, kind);

/** ✓ Complete — the same protected-Undo path as the board's ✓. prevState is
 *  only the Undo target, so best-effort is safe: worst case an Undo lands the
 *  task back In Progress instead of an exotic state. */
function completeFromPalette(t: Taskish): void {
  const known = boardRowOf(t.id) ?? peekTaskDetail(t.id);
  void completeWithUndo(t.id, t.name, known?.state ?? t.state ?? '01_in_progress');
}

/** Assign/unassign one person — a TOGGLE against the resolved current list. */
async function toggleAssignee(t: Taskish, person: Assignee): Promise<void> {
  const cur = await resolveCurrent(t.id);
  if (!cur) { toast('Could not load the task to change who’s assigned.', 'error'); return; }
  const has = cur.assignees.some((a) => a.id === person.id);
  const next = has ? cur.assignees.filter((a) => a.id !== person.id) : [...cur.assignees, person];
  const ok = await patchTask(t.id, { assigneeIds: next.map((a) => a.id) }, { assignees: next });
  if (ok) toast(has ? `Unassigned ${person.name}.` : `Assigned to ${person.name}.`);
}

async function togglePriority(t: Taskish): Promise<void> {
  const cur = await resolveCurrent(t.id);
  if (!cur) { toast('Could not load the task to set priority.', 'error'); return; }
  const next: Priority = cur.priority === '1' ? '0' : '1';
  const ok = await patchTask(t.id, { priority: next }, { priority: next });
  if (ok) toast(next === '1' ? 'Marked high priority.' : 'Priority cleared.');
}

/** Local YYYY-MM-DD, n days ahead — built by hand, NOT via toISOString(): a
 *  UTC round-trip shifts the day anywhere west of UTC (the Dashboard's own
 *  isoDaysAgo guards the same trap, in reverse). */
function ymdIn(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function setDue(t: Taskish, days: number | null, label: string): void {
  const deadline = days === null ? null : ymdIn(days);
  void patchTask(t.id, { deadline }, { deadline })
    .then((ok) => { if (ok) toast(days === null ? 'Due date cleared.' : `Due ${label}.`); });
}

function moveToStage(t: Taskish, s: Stage): void {
  // moveStage raises its own protected toast when a drop into a Done column
  // auto-completes; this routine confirmation yields to it by design.
  void moveStage(t.id, s.id).then((ok) => { if (ok) toast(`Moved to “${s.name}”.`); });
}

function copyTaskLink(id: number): void {
  void navigator.clipboard.writeText(taskLink(id))
    .then(() => toast('Link copied — paste it anywhere.'))
    .catch(() => toast('Could not copy the link.', 'error'));
}

/** Which palette level is showing: the results list, or a verb/submenu level
 *  acting on one task. Query + results state stay mounted underneath, so '←'
 *  returns to exactly where you were. */
type Mode =
  | { kind: 'results' }
  | { kind: 'verbs'; t: Taskish }
  | { kind: 'assign'; t: Taskish }
  | { kind: 'stage'; t: Taskish };

interface ActionItem {
  key: string;
  label: string;
  icon?: LucideIcon;
  person?: Assignee;
  /** ✓ — already assigned / the current stage. */
  check?: boolean;
  /** True = run() opens a deeper level instead of executing. */
  submenu?: boolean;
  run: () => void;
}

/** Highlight from server-provided offsets — the snippet is plain text, so this
 *  never renders untrusted markup. */
function Snippet({ text, matches }: { text: string; matches: Array<[number, number]> }) {
  if (!matches.length) return <>{text}</>;
  const out: React.ReactNode[] = [];
  let at = 0;
  matches.forEach(([s, e], i) => {
    if (s > at) out.push(text.slice(at, s));
    out.push(<mark key={i} className="rounded bg-brand/25 px-0.5 text-ink">{text.slice(s, e)}</mark>);
    at = e;
  });
  if (at < text.length) out.push(text.slice(at));
  return <>{out}</>;
}

export function SearchModal({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [tasks, setTasks] = useState<TaskRef[]>([]);
  const [status, setStatus] = useState<SearchStatus>('all');
  const [deep, setDeep] = useState(false);
  const [deepRes, setDeepRes] = useState<DeepSearchResult>({ descriptions: [], comments: [] });
  const [loading, setLoading] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [mode, setMode] = useState<Mode>({ kind: 'results' });
  const [verbCursor, setVerbCursor] = useState(0);
  // Read once per open — the ring only changes when a task opens, which closes us.
  const [recents] = useState<RecentTask[]>(readRecents);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const areas = useAreaStore((s) => s.areas);
  const selectArea = useAreaStore((s) => s.selectArea);
  const stagesByArea = useAreaStore((s) => s.stagesByArea);
  const stagesLoadingMap = useAreaStore((s) => s.stagesLoading);
  const assignees = useLookupStore((s) => s.assignees);
  const lookupLoading = useLookupStore((s) => s.loading);
  const tasksByArea = useTasksStore((s) => s.tasksByArea);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Esc closes from ANYWHERE in the modal. It used to be handled only on the
  // <input>, so one click on a filter chip — or on the panel background, which
  // blurs the field — left Esc dead while the modal still showed an `esc` key.
  // fromInputs: the text box is where the cursor lives here, so the usual
  // "don't close while typing" rule would disable the shortcut entirely.
  useEscapeToClose(onClose, { fromInputs: true });

  // Responses can land out of order while typing — only the newest may write state.
  const seqRef = useRef(0);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { seqRef.current++; setTasks([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      const seq = ++seqRef.current;
      api.get<{ results: TaskRef[] }>(`/search?q=${encodeURIComponent(query)}&status=${status}`)
        .then((r) => { if (seq === seqRef.current) setTasks(r.results); })
        .catch(() => { if (seq === seqRef.current) setTasks([]); })
        .finally(() => { if (seq === seqRef.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, status]);

  const deepSeqRef = useRef(0);
  useEffect(() => {
    const query = q.trim();
    if (!deep || query.length < 2) { deepSeqRef.current++; setDeepRes({ descriptions: [], comments: [] }); setDeepLoading(false); return; }
    setDeepLoading(true);
    const t = setTimeout(() => {
      const seq = ++deepSeqRef.current;
      api.get<DeepSearchResult>(`/search/deep?q=${encodeURIComponent(query)}&status=${status}`)
        .then((r) => { if (seq === deepSeqRef.current) setDeepRes(r); })
        .catch(() => { if (seq === deepSeqRef.current) setDeepRes({ descriptions: [], comments: [] }); })
        .finally(() => { if (seq === deepSeqRef.current) setDeepLoading(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [q, deep, status]);

  const rows = useMemo<Row[]>(() => {
    const query = q.trim().toLowerCase();
    if (query.length < 2) {
      // ZERO QUERY: the ring of recent tasks, then the Go-to commands. The
      // fetch effects above have already cleared tasks/deepRes for short queries.
      return [
        ...recents.map((task): Row => ({ kind: 'recent', task })),
        ...COMMANDS.map((cmd): Row => ({ kind: 'command', cmd })),
      ];
    }
    const areaRows: Row[] = areas.filter((a) => a.name.toLowerCase().includes(query)).slice(0, 4).map((a) => ({ kind: 'area', id: a.id, name: a.name }));
    return [
      ...areaRows,
      ...tasks.map((task): Row => ({ kind: 'task', task })),
      ...deepRes.descriptions.map((hit): Row => ({ kind: 'desc', hit })),
      ...deepRes.comments.map((hit): Row => ({ kind: 'comment', hit })),
    ];
  }, [q, areas, tasks, deepRes, recents]);

  // Reset the highlight when the QUERY or filters change - NOT when deep results
  // land, which would yank the cursor out from under someone already arrowing down.
  useEffect(() => { setCursor(0); }, [q, status, deep]);

  useEffect(() => { setCursor((c) => Math.min(c, Math.max(0, rows.length - 1))); }, [rows.length]);

  /** Enter a verb/submenu level: warm what it needs, land at its first row. */
  const enterMode = (m: Mode) => {
    if (m.kind !== 'results') {
      prefetchTask(m.t.id); // resolveCurrent's cache hit at pick time
      if (m.kind === 'assign') void useLookupStore.getState().load();
      if (m.kind === 'stage') void useAreaStore.getState().loadStages(m.t.areaId);
    }
    setMode(m);
    setVerbCursor(0);
  };

  const goBack = () => {
    if (mode.kind === 'assign' || mode.kind === 'stage') { setMode({ kind: 'verbs', t: mode.t }); setVerbCursor(0); }
    else if (mode.kind === 'verbs') setMode({ kind: 'results' });
  };

  /** Leaf verbs execute and CLOSE (their toasts confirm from outside);
   *  submenu rows only descend. */
  const closeAnd = (fn: () => void) => { fn(); onClose(); };

  const actionItems = useMemo<ActionItem[]>(() => {
    if (mode.kind === 'results') return [];
    const t = mode.t;
    if (mode.kind === 'verbs') {
      const known = boardRowOf(t.id) ?? peekTaskDetail(t.id);
      return [
        { key: 'complete', icon: Check, label: 'Complete', run: () => closeAnd(() => completeFromPalette(t)) },
        { key: 'assign', icon: Users, label: 'Assign to…', submenu: true, run: () => enterMode({ kind: 'assign', t }) },
        { key: 'stage', icon: Kanban, label: 'Move to stage…', submenu: true, run: () => enterMode({ kind: 'stage', t }) },
        { key: 'priority', icon: Star, label: known?.priority === '1' ? 'Clear priority' : 'Set priority', run: () => closeAnd(() => void togglePriority(t)) },
        { key: 'due0', icon: CalendarDays, label: 'Due today', run: () => closeAnd(() => setDue(t, 0, 'today')) },
        { key: 'due1', icon: CalendarDays, label: 'Due tomorrow', run: () => closeAnd(() => setDue(t, 1, 'tomorrow')) },
        { key: 'due7', icon: CalendarDays, label: 'Due next week', run: () => closeAnd(() => setDue(t, 7, 'next week')) },
        { key: 'dueX', icon: X, label: 'Clear due date', run: () => closeAnd(() => setDue(t, null, '')) },
        { key: 'copy', icon: LinkIcon, label: 'Copy link', run: () => closeAnd(() => copyTaskLink(t.id)) },
      ];
    }
    if (mode.kind === 'assign') {
      const cur = new Set(((boardRowOf(t.id) ?? peekTaskDetail(t.id))?.assignees ?? []).map((a) => a.id));
      return assignees.map((p): ActionItem => ({
        key: `p${p.id}`,
        label: p.name,
        person: p,
        check: cur.has(p.id),
        run: () => closeAnd(() => void toggleAssignee(t, p)),
      }));
    }
    const curStage = (boardRowOf(t.id) ?? peekTaskDetail(t.id))?.stageId;
    return (stagesByArea[t.areaId] ?? []).map((s): ActionItem => ({
      key: `s${s.id}`,
      label: s.name,
      check: s.id === curStage,
      run: () => closeAnd(() => moveToStage(t, s)),
    }));
    // tasksByArea: the ✓ marks read the board rows, so a live update refreshes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, assignees, stagesByArea, tasksByArea]);

  useEffect(() => { setVerbCursor((c) => Math.min(c, Math.max(0, actionItems.length - 1))); }, [actionItems.length]);

  const pick = (row: Row | undefined) => {
    if (!row) return;
    if (row.kind === 'command') { row.cmd.run(); onClose(); return; }
    // ONE history entry for the whole jump. nav.tx flushes in its own `finally`,
    // so BOTH store mutations have to land inside the synchronous block — a
    // `.then` continuation runs after the flush and pushes a second entry, which
    // then makes the first ✕ a dead click (Back lands on the same taskId).
    // The unsaved-description veto is resolved FIRST, outside the tx, so a Cancel
    // leaves both the project and the task exactly where they were.
    const t = useTaskDetailStore.getState();
    const jump = (taskId: number, areaId: number) => {
      if (!t.confirmOpen(taskId)) return;
      const already = t.openId === taskId && t.task !== null; // don't re-fetch what's on screen
      nav.tx('push', () => {
        if (useAreaStore.getState().selectedAreaId !== areaId) selectArea(areaId);
        if (!already) void t.openFromRoute(taskId); // openId is set before the first await
      });
    };
    if (row.kind === 'area') selectArea(row.id);
    else if (row.kind === 'recent') {
      // A recent may live in a project this account can't OPEN (task-level
      // access, v1.14.1) — selecting that area would bounce to Home with a
      // "not available" toast. Only bring the project along when it's actually
      // in the caller's list; otherwise open the task bare, Home behind it.
      if (t.confirmOpen(row.task.id)) {
        const already = t.openId === row.task.id && t.task !== null;
        const visible = areas.some((a) => a.id === row.task.areaId);
        nav.tx('push', () => {
          if (visible && useAreaStore.getState().selectedAreaId !== row.task.areaId) selectArea(row.task.areaId);
          if (!already) void t.openFromRoute(row.task.id);
        });
      }
    }
    else if (row.kind === 'task') jump(row.task.id, row.task.areaId);
    else jump(row.hit.taskId, row.hit.areaId);
    onClose();
  };

  // Arrowing past the fold must scroll, or the highlight vanishes off-screen.
  // One effect serves both levels — whichever cursor is live drives it.
  const activeIdx = mode.kind === 'results' ? cursor : verbCursor;
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, mode.kind]);

  const chip = (active: boolean) =>
    `rounded px-2 py-0.5 text-[11px] font-medium transition ${active ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`;

  // Section headers are rendered inline by comparing each row to the previous one.
  const headerFor = (row: Row, prev: Row | undefined): string | null => {
    if (row.kind === 'recent' && prev?.kind !== 'recent') return 'Recent';
    if (row.kind === 'command' && prev?.kind !== 'command') return 'Go to';
    if (row.kind === 'desc' && prev?.kind !== 'desc') return 'In descriptions';
    if (row.kind === 'comment' && prev?.kind !== 'comment') return 'In comments & notes';
    return null;
  };

  const areaNameById = useMemo(() => new Map(areas.map((a) => [a.id, a.name])), [areas]);

  /** The '→' affordance on the highlighted row — the mouse door into the verb
   *  layer. A span (not a button): these rows ARE buttons, and a button may
   *  not contain another. */
  const actionsHint = (row: Row, i: number) => {
    const tk = taskishOf(row);
    if (!tk || i !== cursor) return null;
    return (
      <span
        onClick={(e) => { e.stopPropagation(); enterMode({ kind: 'verbs', t: tk }); }}
        title="Actions (→)"
        className="ml-1 shrink-0 cursor-pointer"
      >
        <Kbd>→</Kbd>
      </span>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-24 z-[70] w-[560px] max-w-[92vw] -translate-x-1/2 overflow-hidden rounded-xl border border-line bg-canvas shadow-2xl">
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Icon i={Search} size={14} className="text-faint" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              // Typing (or pasting) while a verb level is up drops straight back
              // into search — the keystroke lands in the box, nothing is eaten.
              if (mode.kind !== 'results') setMode({ kind: 'results' });
              setQ(e.target.value);
            }}
            onKeyDown={(e) => {
              // Escape is handled at the window (see useEscapeToClose above) so it
              // works whatever has focus; it never reaches this handler.
              if (mode.kind !== 'results') {
                // Verb/submenu level: the list below is the thing being driven.
                if (e.key === 'ArrowDown') { e.preventDefault(); setVerbCursor((c) => Math.min(c + 1, Math.max(0, actionItems.length - 1))); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setVerbCursor((c) => Math.max(c - 1, 0)); }
                else if (e.key === 'Enter') { e.preventDefault(); actionItems[verbCursor]?.run(); }
                else if (e.key === 'ArrowRight') { const it = actionItems[verbCursor]; if (it?.submenu) { e.preventDefault(); it.run(); } }
                else if (e.key === 'ArrowLeft' || e.key === 'Backspace') { e.preventDefault(); goBack(); }
                else if (e.key === 'Tab') { e.preventDefault(); } // Deep stays put while acting
                // Any printable key falls through: onChange above exits to results.
                return;
              }
              if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); pick(rows[cursor]); }
              else if (e.key === 'ArrowRight') {
                // Into the verb layer — but ONLY when the caret has nowhere left
                // to go, so '→' keeps moving through text like any input.
                const el = e.currentTarget;
                const atEnd = el.selectionStart === null || el.selectionStart === el.value.length;
                const tk = taskishOf(rows[cursor]);
                if (atEnd && tk) { e.preventDefault(); enterMode({ kind: 'verbs', t: tk }); }
              }
              // Plain Tab toggles Deep; Shift+Tab stays a normal focus move so the
              // chips and results remain keyboard-reachable.
              else if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); setDeep((d) => !d); }
            }}
            placeholder="Search tasks and projects…"
            className="w-full bg-transparent py-3 text-[14px] text-ink outline-none placeholder:text-faint"
          />
          {/* A real button, not a <kbd>. It was decorative markup that looked
              exactly like something you press — so clicking it did nothing, which
              is how Pat hit this: the keyboard worked, the badge didn't. If it
              looks pressable it has to BE pressable. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            title="Close (Esc)"
            className="shrink-0 rounded border border-line px-1.5 py-0.5 text-[10px] text-faint transition hover:border-(--color-line-strong) hover:bg-hover hover:text-ink"
          >
            esc
          </button>
        </div>

        {mode.kind === 'results' ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-1.5">
            <nav className="flex items-center gap-0.5 rounded-md border border-line bg-page p-0.5">
              {([['all', 'All'], ['open', 'Open'], ['closed', 'Done']] as const).map(([v, label]) => (
                <button key={v} onClick={() => setStatus(v)} className={chip(status === v)}>{label}</button>
              ))}
            </nav>
            <button
              onClick={() => setDeep((d) => !d)}
              title="Also search inside descriptions and comments"
              className={`rounded-md border px-2 py-0.5 text-[11px] font-medium transition ${deep ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted hover:bg-hover hover:text-ink'}`}
            >
              Deep {deep ? 'on' : '⇥'}
            </button>
            {!deep && <span className="text-[10px] text-faint">⇥ also search descriptions &amp; comments</span>}
            {deepLoading && <span className="text-[10px] text-faint">searching content…</span>}
          </div>
        ) : (
          <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
            <button
              onClick={goBack}
              className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted transition hover:bg-hover hover:text-ink"
            >
              <Icon i={ChevronLeft} size={14} /> Back
            </button>
            <span className="min-w-0 truncate text-[11px] text-faint">
              {mode.t.name}
              {mode.kind === 'assign' ? ' · assign to' : mode.kind === 'stage' ? ' · move to stage' : ''}
            </span>
            <span className="ml-auto shrink-0 text-[10px] text-faint">↵ run · ← back</span>
          </div>
        )}

        <div ref={listRef} className="max-h-[380px] overflow-y-auto py-1">
          {mode.kind !== 'results' ? (
            <>
              {actionItems.map((it, i) => (
                <div key={it.key} data-row={i}>
                  <button
                    onClick={it.run}
                    onMouseEnter={() => setVerbCursor(i)}
                    className={`flex h-8 w-full items-center gap-2 px-3 text-left text-sm text-ink ${i === verbCursor ? 'bg-hover' : ''}`}
                  >
                    {it.person
                      ? <Avatar person={it.person} size={18} />
                      : it.icon ? <Icon i={it.icon} size={14} className="text-muted" /> : null}
                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    {it.check && <Icon i={Check} size={14} className="text-success" />}
                    {it.submenu && <Icon i={ChevronRight} size={14} className="text-faint" />}
                  </button>
                </div>
              ))}
              {mode.kind === 'assign' && actionItems.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-faint">{lookupLoading ? 'Loading people…' : 'No people to show.'}</div>
              )}
              {mode.kind === 'stage' && actionItems.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-faint">
                  {stagesLoadingMap[mode.t.areaId] ? 'Loading stages…' : 'Stages unavailable for this project.'}
                </div>
              )}
            </>
          ) : (
            <>
              {q.trim().length < 2 && recents.length === 0 && (
                <div className="px-3 pb-1 pt-3 text-center text-xs text-faint">Tasks you open will appear here for quick reopening.</div>
              )}
              {q.trim().length >= 2 && !loading && !deepLoading && rows.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-faint">
                  No matches.{!deep && ' Press ⇥ to also search inside descriptions and comments.'}
                </div>
              )}
              {rows.map((row, i) => {
                const header = headerFor(row, rows[i - 1]);
                const key = row.kind === 'recent' ? `r${row.task.id}`
                  : row.kind === 'command' ? `cmd${row.cmd.id}`
                  : row.kind === 'area' ? `a${row.id}`
                  : row.kind === 'task' ? `t${row.task.id}`
                  : row.kind === 'desc' ? `d${row.hit.taskId}`
                  : `c${row.hit.taskId}-${row.hit.date}-${i}`;
                return (
                  <div key={key} data-row={i}>
                    {header && <div className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-faint">{header}</div>}
                    <button
                      onClick={() => pick(row)}
                      onMouseEnter={() => {
                        setCursor(i);
                        // Project and command rows have no task detail to warm.
                        const tk = taskishOf(row);
                        if (tk) hoverStart(tk.id);
                      }}
                      onMouseLeave={hoverEnd}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left ${i === cursor ? 'bg-hover' : ''}`}
                    >
                      {row.kind === 'recent' ? (
                        <>
                          <Icon i={History} size={14} className="mt-0.5 text-faint" />
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{row.task.name}</span>
                          {areaNameById.has(row.task.areaId) && (
                            <span className="shrink-0 text-[11px] text-faint">{areaNameById.get(row.task.areaId)}</span>
                          )}
                          {actionsHint(row, i)}
                        </>
                      ) : row.kind === 'command' ? (
                        <>
                          <Icon i={row.cmd.icon} size={14} className="mt-0.5 text-muted" />
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{row.cmd.label}</span>
                          <Kbd>{row.cmd.kbd}</Kbd>
                        </>
                      ) : row.kind === 'area' ? (
                        <>
                          <span className="text-[11px] text-faint">▦</span>
                          <span className="truncate text-[13px] font-medium text-ink">{row.name}</span>
                          <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-faint">project</span>
                        </>
                      ) : row.kind === 'task' ? (
                        <>
                          <span className={`min-w-0 flex-1 truncate text-[13px] ${CLOSED.has(row.task.state) ? 'text-muted' : 'text-ink'}`}>{row.task.name}</span>
                          {row.task.archived && <span className="shrink-0 rounded border border-line px-1 text-[10px] text-faint">archived</span>}
                          <span className="shrink-0 text-[11px] text-faint">{row.task.areaName}</span>
                          <StateBadge state={row.task.state} />
                          {actionsHint(row, i)}
                        </>
                      ) : (
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className={`min-w-0 flex-1 truncate text-[13px] ${CLOSED.has(row.hit.state) ? 'text-muted' : 'text-ink'}`}>{row.hit.name}</span>
                            {row.kind === 'comment' && row.hit.isNote && (
                              <span className="shrink-0 rounded border border-brand/50 px-1 text-[10px] text-brand">note</span>
                            )}
                            <span className="shrink-0 text-[11px] text-faint">{row.hit.areaName}</span>
                            {actionsHint(row, i)}
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-muted">
                            {row.kind === 'comment' && <span className="text-faint">{row.hit.authorName}: </span>}
                            <Snippet text={row.hit.snippet} matches={row.hit.matches} />
                          </span>
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
              {deepRes.truncated && (
                <div className="px-3 py-2 text-center text-[10px] text-faint">
                  Showing the strongest recent matches — narrow the words or pick a project to see everything.
                </div>
              )}
              {loading && <div className="px-3 py-2 text-center text-[11px] text-faint">Searching…</div>}
            </>
          )}
        </div>
      </div>
    </>
  );
}
